import { LitElement, css, html } from "lit";
import { ref, createRef } from "lit/directives/ref.js";
import { customElement, property } from "lit/decorators.js";

import "@shoelace-style/shoelace/dist/components/popup/popup.js";
import "@shoelace-style/shoelace/dist/components/animation/animation.js";
import { SlPopup } from "@shoelace-style/shoelace";

type CoordsAtPosFunc = (pos: number) => {
    top: number;
    bottom: number;
    left: number;
    right: number;
};

export const TAG_NAME = "c-editor-dynamic-popup";

/**
 * A popup that is positioned at a positionin the document using JavaScript,
 * while existing outside the DOM managed by ProseMirror.
 *
 * Constructed with two positions in the document to which it will be anchored,
 * and a function that converts document positions into viewports coordinates.
 */

@customElement(TAG_NAME)
class EditorDynamicPopup extends LitElement {
    @property({ attribute: "data-document-start-pos", reflect: true })
    startPos: number;

    @property({ attribute: "data-document-end-pos", reflect: true })
    endPos: number;

    @property({ attribute: "data-popup-key", reflect: true })
    key: string;

    popupRef = createRef<SlPopup>();

    coordsAtDocumentPosition: CoordsAtPosFunc;

    close: () => void;
    closeOnMouseLeave: boolean;

    static styles = css`
        :root {
            white-space: normal;
        }
    `;

    constructor({
        pos,
        key,
        coordsAtDocumentPosition,
        close,
        closeOnMouseLeave = true,
    }: {
        pos: {
            start: number;
            end: number;
        };
        key: string;
        coordsAtDocumentPosition: CoordsAtPosFunc;
        close: () => void;
        closeOnMouseLeave: boolean | undefined;
    }) {
        super();
        this.startPos = pos.start;
        this.endPos = pos.end;
        this.coordsAtDocumentPosition = coordsAtDocumentPosition;
        this.close = close;
        this.closeOnMouseLeave = closeOnMouseLeave;
        this.key = key;
    }

    anchor() {
        return {
            getBoundingClientRect: () => {
                try {
                    const startCoords = this.coordsAtDocumentPosition(
                        this.startPos
                    );
                    const endCoords = this.coordsAtDocumentPosition(
                        this.endPos
                    );

                    return new DOMRect(
                        startCoords.left,
                        startCoords.top,
                        endCoords.left - startCoords.left,
                        endCoords.bottom - startCoords.top
                    );
                } catch {
                    // Anchor is not available anymore.
                    return {};
                }
            },
        };
    }

    private _handleMouseLeave() {
        if (this.closeOnMouseLeave) {
            this.close();
        }
    }

    updated() {
        this.popupRef.value?.reposition();
    }

    render() {
        return html`<sl-animation
            name="fadeIn"
            easing="ease-out"
            duration="150"
            iterations="1"
            play
        >
            <sl-popup
                ${ref(this.popupRef)}
                placement="bottom-start"
                distance="10"
                hover-bridge
                active
                arrow
                .anchor=${this.anchor()}
                flip
                shift
                auto-size
                @mouseleave=${this._handleMouseLeave}
            >
                <slot name="contents"></slot>
            </sl-popup>
        </sl-animation>`;
    }
}

export { EditorDynamicPopup };
